import React from 'react';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import { useForm, useFormState } from 'react-final-form';
import {
  FormTableFieldType,
  neuroConsentV2BillTypeFieldsBase,
  BillingType,
  getPaymentPlanOptions,
  OTHER_OPTION,
  neuroConsentV2InsuranceFieldsBase,
  RELATIONSHIP_OPTIONS,
  IN_NETWORK_INSURANCES,
  GOVERNMENT_INSURANCES,
} from '@tempus/patient-forms-service-shared';
import { FinancialAssistanceStatuses, InsuranceType } from 'requisition-service-shared';
import FormTable, { FormTableField, FormTableFieldFrontend } from '../../shared/FormTable';
import { REQUIRED_FIELD_WARNING } from '../../shared/validation-utils';
import StripeWidget from '../../../../../components/StripeWidget';
import { combineFormConfigs } from '../../shared/utils';
import classNames from 'classnames';
import standaloneStyles from '../../shared/standalone-styles.module.css';

type BillingInformationFormProps = {
  faStatus: FinancialAssistanceStatuses | null;
  stripeCheckoutToken: string;
  setStripeComplete: (value: boolean) => void;
  shouldCollectGovernmentInsurance: boolean;
};

export const BillingInformationForm: React.FunctionComponent = () => {
  const form = useForm();
  const billingInformationFieldsFrontend: FormTableFieldFrontend[] = [
    {
      name: 'billingType',
      props: {
        placeholder: 'Select Billing Type',
        dropdownOptions: [
          { label: 'Self-Pay', value: BillingType.SELF_PAY },
          { label: 'Government Insurance', value: BillingType.PUBLIC_INSURANCE },
          { label: 'Commercial Insurance', value: BillingType.COMMERCIAL_INSURANCE },
        ],
        defaultValue: BillingType.SELF_PAY,
        onChange: (value: any) => {
          form.restart();
          form.change('billingType', value.value);
        },
      },
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
      },
    },
  ];

  const neuroConsentV2BillTypeFields = combineFormConfigs(
    billingInformationFieldsFrontend,
    neuroConsentV2BillTypeFieldsBase,
  );
  return (
    <div className={standaloneStyles.formInputContainer}>
      <a
        className={classNames(typography.subSectionHeader, typography.link)}
        href="/nP_Patient-Billing-Guide.pdf"
        target="_blank"
        rel="noopener noreferrer"
      >
        Billing Information
      </a>
      <FormTable fields={neuroConsentV2BillTypeFields} />
    </div>
  );
};

export const InsuranceInformationSubform: React.FC<{ insuranceType: InsuranceType }> = ({ insuranceType }) => {
  const standardConditionalValueConfig = {
    fieldNameToCheck: 'insuranceCompanyName',
    valueToCheck: OTHER_OPTION.value,
    conditionalLogic: (value: any, conditionalValue: string) => {
      return !(value === conditionalValue || value === '');
    },
  };
  const insuranceInformationFieldsFrontend: FormTableFieldFrontend[] = [
    {
      name: 'insuranceCompanyName',
      props: {
        placeholder: 'Select Insurance Plan Type',
        dropdownOptions: (insuranceType === InsuranceType.Public ? IN_NETWORK_INSURANCES : GOVERNMENT_INSURANCES)
          .map((insurance) => ({
            label: insurance,
            value: insurance,
          }))
          .concat(insuranceType === InsuranceType.Private ? OTHER_OPTION : []),
      },
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
      },
    },
    {
      name: 'insuranceProviderName',
      validate: (value: any | undefined) => {
        const CHARACTER_LIMIT = 40;
        if (!value) return REQUIRED_FIELD_WARNING;
        if (value.length > CHARACTER_LIMIT)
          return `Insurance Provider Name must be less than ${CHARACTER_LIMIT} characters`;
      },
      conditionalValueConfig: {
        ...standardConditionalValueConfig,
        additionalConditions: [
          {
            fieldNameToCheck: 'billingType',
            valueToCheck: BillingType.PUBLIC_INSURANCE,
            conditionalLogic: (value: any, conditionalValue: string) => {
              return value === conditionalValue;
            },
          },
        ],
      },
    },
    {
      name: 'policyNumber',
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
      },
      conditionalValueConfig: standardConditionalValueConfig,
    },
    {
      name: 'groupNumber',
      conditionalValueConfig: standardConditionalValueConfig,
    },
    {
      name: 'policyHolderName',
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
      },
      conditionalValueConfig: standardConditionalValueConfig,
    },
    {
      name: 'relationshipToPolicyHolder',
      props: {
        placeholder: 'Select Relationship to Policy Holder',
        dropdownOptions: RELATIONSHIP_OPTIONS,
      },
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
      },
      conditionalValueConfig: standardConditionalValueConfig,
    },
  ];

  const neuroConsentV2InsuranceFields: FormTableField[] = combineFormConfigs(
    insuranceInformationFieldsFrontend,
    neuroConsentV2InsuranceFieldsBase,
  );

  return (
    <div className={standaloneStyles.formInputContainer}>
      <div className={typography.subSectionHeader}>Insurance Information</div>
      <FormTable fields={neuroConsentV2InsuranceFields} />
    </div>
  );
};

export const CommercialInsurancePaymentPlanSubform: React.FunctionComponent<BillingInformationFormProps> = ({
  faStatus,
  stripeCheckoutToken,
  setStripeComplete,
}) => {
  const formState = useFormState();
  const insuranceCompanyName = formState.values['insuranceCompanyName'];
  const forceSelfPayFlow = insuranceCompanyName === OTHER_OPTION.value;
  const paymentPlanOptions = getPaymentPlanOptions(
    faStatus,
    forceSelfPayFlow ? BillingType.SELF_PAY : BillingType.COMMERCIAL_INSURANCE,
  );
  const paymentPlanField: FormTableField[] = [
    {
      label: 'Payment Plan',
      name: 'numberOfInstallments',
      props: {
        placeholder: 'Select Payment Plan',
        dropdownOptions: paymentPlanOptions,
      },
      type: FormTableFieldType.SINGLE_SELECT_DROPDOWN,
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
        if (!paymentPlanOptions.some((option) => option.value === value)) {
          return REQUIRED_FIELD_WARNING;
        }
      },
    },
  ];

  return (
    <>
      <div className={standaloneStyles.formInputContainer}>
        {insuranceCompanyName && (
          <>
            <FormTable fields={paymentPlanField} />
            <StripeWidget
              onComplete={() => {
                setStripeComplete(true);
              }}
              checkoutSessionSecret={stripeCheckoutToken}
            />
          </>
        )}
      </div>
    </>
  );
};

export const SelfPayPaymentPlanSubform: React.FunctionComponent<BillingInformationFormProps> = ({
  faStatus,
  stripeCheckoutToken,
  setStripeComplete,
}) => {
  const paymentPlanOptions = getPaymentPlanOptions(faStatus, BillingType.SELF_PAY);
  const selfPayPaymentPlanOptions: FormTableField[] = [
    {
      label: 'Payment Plan',
      name: 'numberOfInstallments',
      props: {
        placeholder: 'Select Payment Plan',
        dropdownOptions: paymentPlanOptions,
      },
      type: FormTableFieldType.SINGLE_SELECT_DROPDOWN,
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
      },
    },
  ];
  return (
    <>
      <div className={standaloneStyles.formInputContainer}>
        <FormTable fields={selfPayPaymentPlanOptions} />
        <StripeWidget
          onComplete={() => {
            setStripeComplete(true);
          }}
          checkoutSessionSecret={stripeCheckoutToken}
        />
      </div>
    </>
  );
};

export default BillingInformationForm;
